/**
 * ClientlessAuthErrors
 */

import {
  AuthError,
  AuthErrorBuilder,
  AuthErrorCategory,
  AuthErrorDomain,
  AuthErrorNumeral,
  AuthErrorSeverity,
} from './AuthError'
import { isDcrAccessTokenError, isDcrRegistrationError, isRestError } from './util/Predicates'
import { ErrorInformation } from './api-clients/rest-api-v2/types.gen'

/**
 * All possible enhanced error codes that can occur from REST v2 API calls
 * @see RestError.code
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-lists-rest-api-v2
 */
export const RestErrorCodeValues = [
  'invalid_parameter_service_provider',
  'invalid_parameter_mvpd',
  'invalid_parameter_code',
  'invalid_parameter_resources',
  'invalid_parameter_redirect_url',
  'invalid_parameter_partner',
  'invalid_parameter_saml_response',
  'invalid_header_device_info',
  'invalid_header_device_identifier',
  'invalid_header_identity_for_temporary_access',
  'invalid_header_pfs_permission_access_not_present',
  'invalid_header_pfs_permission_access_not_determined',
  'invalid_header_pfs_permission_access_not_granted',
  'invalid_header_pfs_provider_id_not_determined',
  'invalid_header_pfs_provider_id_mismatch',
  'invalid_header_pfs_provider_info_expired',
  'invalid_integration',
  'invalid_authentication_session',
  'preauthorization_denied_by_mvpd',
  'authorization_denied_by_mvpd',
  'authorization_denied_by_parental_controls',
  'authorization_denied_by_degradation_rule',
  'internal_server_error',
  'too_many_resources',
  'invalid_configuration_user_metadata_certificate',
  'invalid_configuration_temporary_access',
  'invalid_configuration_platform',
  'invalid_configuration_platform_id',
  'invalid_configuration_platform_trait',
  'invalid_configuration_platform_category_trait',
  'invalid_configuration_platform_services',
  'invalid_configuration_mvpd_platform',
  'invalid_configuration_mvpd_platform_boarding_status',
  'invalid_configuration_mvpd_platform_profile_exchange',
  'invalid_access_token_service_provider',
  'invalid_access_token_client_application',
  'authenticated_profile_missing',
  'authenticated_profile_expired',
  'authenticated_profile_invalidated',
  'temporary_access_duration_limit_exceeded',
  'temporary_access_resources_limit_exceeded',
  'authorization_denied_by_hba_policies',
  'authorization_denied_by_session_invalidated',
  'identity_not_recognized_by_mvpd',
  'network_received_error',
  'network_connection_timeout',
  'maximum_execution_time_exceeded',
] as const

/**
 * Provides an Adobe Pass Authentication unique identifier associated with the error
 */
export type RestErrorCode = typeof RestErrorCodeValues[number]

/**
 * All possible enhanced error actions that can occur from REST v2 API calls
 * @see RestError.action
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-action
 */
export const RestErrorActionValues = [
  'none',
  'configuration',
  'application-registration',
  'authentication',
  'authorization',
  'retry',
] as const

/**
 * The recommended action that might remediate the situation
 */
export type RestErrorAction = typeof RestErrorActionValues[number]

/**
 * All possible enhanced error status values that can occur from REST v2 API calls
 * @see RestError.status
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-status
 */
export const RestErrorStatusValues = [
  400, 401, 403, 404, 405, 410, 412, 500
] as const

/**
 * Indicates the HTTP status code associated with the error
 */
export type RestErrorStatus = typeof RestErrorStatusValues[number]

/**
 * The expected type for any Adobe error that is received from the REST v2 API calls
 * 
 * This is a narrower _(more type accurate)_ representation of {@link ErrorInformation}
 * 
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-structure
 */
export type RestError = {
  action: RestErrorAction
  code: RestErrorCode
  status: RestErrorStatus
  details?: string
  helpUrl?: string
  message?: string
  trace?: string
}

/**
 * All possible error codes that can occur from Client Registration DCR calls
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/rest-apis/rest-api-dcr/rest-api-dcr-apis/dynamic-client-registration-apis-retrieve-client-credentials#error
 */
export const ClientRegistrationErrorValues = [ 
  'invalid_request',
  'invalid_redirect_uri',
  'invalid_software_statement',
  'unapproved_software_statement'
] as const

/**
 * All possible error codes that can occur from Access Token DCR calls
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/rest-apis/rest-api-dcr/rest-api-dcr-apis/dynamic-client-registration-apis-retrieve-access-token#error
 */
export const AccessTokenErrorValues = [ 
  'invalid_request',
  'invalid_client',
  'unauthorized_client',
  'unsupported_grant_type'
] as const

/** @hidden */
export const DcrErrorValues = [
  ...ClientRegistrationErrorValues,
  ...AccessTokenErrorValues
] as const

/** @hidden */
export type ClientRegistrationErrorValue = typeof ClientRegistrationErrorValues[number]
 
/** @hidden */
export type AccessTokenErrorValue = typeof AccessTokenErrorValues[number]

/** @hidden */
export type DcrErrorValue = ClientRegistrationErrorValue | AccessTokenErrorValue

/**
 * The expected type for any Adobe error that is received from the DCR calls
 * @hidden
 */
export interface DcrError {
  error: DcrErrorValue
}

/**
 * A narrower version of {@link DcrError} which only occurs if an error is
 * encountered during client registration
 * @hidden
 */
export interface ClientRegistrationError {
  error: ClientRegistrationErrorValue
}

/**
 * A narrower version of {@link DcrError} which only occurs if an error is
 * encountered during access token retrieval
 * @hidden
 */
export interface AccessTokenError {
  error: AccessTokenErrorValue
}

/** @hidden */
export class ClientlessAuthErrorBuilder extends AuthErrorBuilder {
  constructor() {
    super()
    this.withDomain(AuthErrorDomain.Adobe)
  }
}

/** @hidden */
export const createResumeAuthenticationUrlParseError = (parseError: unknown): AuthError => {
  const authError = new ClientlessAuthErrorBuilder()
    .withMessage(String(parseError) || 'Authentication Url response data JSON parse failure')
    .withCategory(AuthErrorCategory.Authentication)
    .withNumeral(AuthErrorNumeral.ClientlessResumeAuthenticationUrlInvalidResponseBody)
    .build()
  return authError
}

/** @hidden */
export const createResumeAuthenticationUrlMissingParamsError = (missingParams: string[]): AuthError => {
  return new ClientlessAuthErrorBuilder()
    .withMessage(`Response says we failed to include the required parameters: ${missingParams.join(', ')}.`)
    .withCategory(AuthErrorCategory.Authentication)
    .withNumeral(AuthErrorNumeral.ClientlessResumeAuthenticationUrl)
    .build()
}

/** @hidden */
export const createPreauthorizeDecisionError = (error: ErrorInformation): AuthError => {
  const authError = new ClientlessAuthErrorBuilder()
    .withMessage('Preauthorization denied')
    .withSeverity(AuthErrorSeverity.Warn)
    .withCategory(AuthErrorCategory.Authorization)
    .withNumeral(AuthErrorNumeral.NotAuthorized)
    .withSubErrorCode(error.code)
    .withSubErrorMessage(error.details || error.message || '')
    .build()
  return authError
}

/**
 * Factory class to create Auth error objects based on error data passed from
 * the Adobe DCR / REST APIs
 */
export class ClientlessAuthErrorFactory {

  /**
   * Create an `AuthError` object when a DCR call fails
   * @param error The error response provided by Adobe
   * @param url The endpoint url that was called before the error occurred
   * @returns A new `AuthError` object which details the issue
   */
  public static createDcr(error: unknown, url: string): AuthError {
    console.debug(`[ClientlessAuthErrorFactory.createDcr] error: ${JSON.stringify(error)}, url: ${url}`)

    let builder = this.createDcrBuilder()

    switch (true) {
      case isDcrRegistrationError(error, url):
        builder = this.augmentBuilderDcrRegistration(builder, error, url)
        break

      case isDcrAccessTokenError(error, url):
        builder = this.augmentBuilderDcrAccessToken(builder, error, url)
        break

      default:
        builder = this.augmentBuilderDefault(builder, error, url)
    }

    return builder.build()
  }

  private static createDcrBuilder(): AuthErrorBuilder {
    return new ClientlessAuthErrorBuilder()
      .withCategory(AuthErrorCategory.Initialization)
      .withSeverity(AuthErrorSeverity.Fatal)
  }

  /**
   * Create an `AuthError` object when a REST v2 Configuration call fails
   * @param error The error response provided by Adobe
   * @param url The endpoint url that was called before the error occurred
   * @returns A new `AuthError` object which details the issue
   */
  public static createRestConfiguration(error: unknown, url: string): AuthError {
    return this.createRestError(AuthErrorCategory.Config, error, url)
  }

  /**
   * Create an `AuthError` object when a REST v2 Authentication call fails
   * @param error The error response provided by Adobe
   * @param url The endpoint url that was called before the error occurred
   * @returns A new `AuthError` object which details the issue
   */
  public static createRestAuthentication(error: unknown, url: string): AuthError {
    return this.createRestError(AuthErrorCategory.Authentication, error, url)
  }

  /**
   * Create an `AuthError` object when a REST v2 Preauthorization call fails
   * @param error The error response provided by Adobe
   * @param url The endpoint url that was called before the error occurred
   * @returns A new `AuthError` object which details the issue
   */
  public static createRestPreauthorization(error: unknown, url: string): AuthError {
    return this.createRestError(AuthErrorCategory.Preauthorization, error, url)
  }

  /**
   * Create an `AuthError` object when a REST v2 Authorization call fails
   * @param error The error response provided by Adobe
   * @param url The endpoint url that was called before the error occurred
   * @returns A new `AuthError` object which details the issue
   */
  public static createRestAuthorization(error: unknown, url: string): AuthError {
    return this.createRestError(AuthErrorCategory.Authorization, error, url)
  }

  /**
   * Create an `AuthError` object when a REST v2 Logout call fails
   * @param error The error response provided by Adobe
   * @param url The endpoint url that was called before the error occurred
   * @returns A new `AuthError` object which details the issue
   */
  public static createRestLogout(error: unknown, url: string): AuthError {
    return this.createRestError(AuthErrorCategory.Logout, error, url)
  }

  private static createRestError(category: AuthErrorCategory, error: unknown, url: string): AuthError {
    console.debug(`[createRestError]\ncategory: ${this.getCategory(category)}\nurl: ${url}\nerror: ${JSON.stringify(error)}`)

    let builder = this.createRestBuilder(category)
    if (isRestError(error)) {
      builder = this.augmentBuilderRest(builder, category, error, url)
    } else {
      builder = this.augmentBuilderDefault(builder, error, url)
    }
    return builder.build()
  }

  private static createRestBuilder(category: AuthErrorCategory): AuthErrorBuilder {
    return new ClientlessAuthErrorBuilder()
      .withCategory(category)
      .withSeverity(AuthErrorSeverity.Fatal)
  }

  private static augmentBuilderUnknown(builder: AuthErrorBuilder, error: unknown, url: string): AuthErrorBuilder {
    return builder
      .withCategory(AuthErrorCategory.Unknown)
      .withMessage('An Unknown Error occurred')
      .withNumeral(AuthErrorNumeral.ClientlessUnknownError)
      .withSubErrorMessage(`Unknown error: ${JSON.stringify(error)}`)
      .withSubErrorUrl(url)
  }

  private static augmentBuilderDefault(builder: AuthErrorBuilder, error: unknown, url: string): AuthErrorBuilder {
    if (!error) {
      return this.augmentBuilderMissingData(builder, url)
    } 
    return this.augmentBuilderUnknown(builder, error, url)
  }

  private static augmentBuilderMissingData(builder: AuthErrorBuilder, url: string): AuthErrorBuilder {
    return builder
      .withMessage('Missing "data" on the "result"')
      .withNumeral(AuthErrorNumeral.ClientlessAPIUnexpectedResponseCode)
      .withSubErrorUrl(url)
  }

  private static augmentBuilderDcrRegistration(builder: AuthErrorBuilder, regError: ClientRegistrationError, url: string): AuthErrorBuilder {
    const numeral = AuthErrorNumeral.ClientlessClientRegistrationServiceException
    switch (regError.error) {
      case 'invalid_request':
        return builder
          .withMessage('The request is invalid due to one of the following reasons:  1.) The request misses a required parameter  2.) The request includes an unsupported parameter value  3.) The request repeats a parameter  4.) The request is malformed')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please check that all required parameters are provided on the request and that their values are valid and not repeated')
          .withSubErrorCode(regError.error)
          .withSubErrorUrl(url)

      case 'invalid_redirect_uri':
        return builder
          .withMessage('The request includes a value for the redirection URI which is invalid')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please provide a valid \'redirectUri\' value in the Auth configuration')
          .withSubErrorCode(regError.error)
          .withSubErrorUrl(url)

      case 'invalid_software_statement':
        return builder
          .withMessage('The request includes a value for the software statement that is invalid')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please provide a valid \'softwareStatement\' value in the Auth configuration',)
          .withSubErrorCode(regError.error,)
          .withSubErrorUrl(url)

      case 'unapproved_software_statement':
        return builder
          .withMessage('The request includes a value for the software statement which is not approved for use by the Adobe Pass Authentication server')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please provide an approved \'softwareStatement\' value in the Auth configuration')
          .withSubErrorCode(regError.error)
          .withSubErrorUrl(url)

      default:
        return builder
          .withMessage('An Unknown Registration Error occurred')
          .withNumeral(numeral)
          .withSubErrorCode(regError.error)
          .withSubErrorUrl(url)
    }
  }

  private static augmentBuilderDcrAccessToken(builder: AuthErrorBuilder, tokenError: AccessTokenError, url: string): AuthErrorBuilder {
    const numeral = AuthErrorNumeral.ClientlessClientAccessTokenServiceException
    switch (tokenError.error) {
      case 'invalid_request':
        return builder
          .withMessage('The request is invalid due to one of the following reasons:  1.) The request misses a required parameter  2.) The request includes an unsupported parameter value (other than grant type)  3.) The request repeats a parameter  4.) The request includes multiple credentials  5.) The request utilizes more than one mechanism for authenticating the client  6.) The request is malformed')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please check that all required parameters are provided on the request and that their values are valid and not repeated')
          .withSubErrorCode(tokenError.error)
          .withSubErrorUrl(url)

      case 'invalid_client':
        return builder
          .withMessage('The client credentials are invalid, the client needs to obtain new client credentials and try again. For more details refer to the Retrieve client credentials API documentation: https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/rest-apis/rest-api-dcr/rest-api-dcr-apis/dynamic-client-registration-apis-retrieve-client-credentials')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please try clearing cached values in local storage and retry to obtain new client credentials')
          .withSubErrorCode(tokenError.error)
          .withSubErrorUrl(url)

      case 'unauthorized_client':
      case 'unsupported_grant_type':
        return builder
          .withMessage('The grant type used is invalid')
          .withNumeral(numeral)
          .withRecoverySuggestion('Please try clearing cached values in local storage and retry to obtain new client credentials')
          .withSubErrorCode(tokenError.error)
          .withSubErrorUrl(url)

      default:
        return builder
          .withMessage('An Unknown Access Token Error occurred')
          .withNumeral(numeral)
          .withSubErrorCode(tokenError.error)
          .withSubErrorUrl(url)
    }
  }

  private static augmentBuilderRest(builder: AuthErrorBuilder, category: AuthErrorCategory, restError: RestError, url: string): AuthErrorBuilder {
    console.debug('[ClientlessAuthErrorFactory.augmentBuilderRest] restError:', restError, 'url:', url)
    
    const { action, code , message } = restError
    
    const numeral = this.getNumeral(category)
    const suggestion = this.getRecoverySuggestion(action as RestErrorAction)

    builder = builder
      .withMessage(message || 'No error message was provided by the service response')
      .withNumeral(numeral)
      .withSubErrorCode(code)
      .withSubErrorUrl(url)
    
    if (suggestion) {
      builder = builder.withRecoverySuggestion(suggestion)
    }
    
    return builder
  }
  
  private static getNumeral(category: AuthErrorCategory): AuthErrorNumeral {
    const { Config, Authentication, Authorization, Preauthorization, Logout } = AuthErrorCategory
    const { ClientlessConfigServiceException, ClientlessAuthNTokenServiceException, ClientlessAuthorizeServiceException, ClientlessPreauthorizeServiceException, AdobeLogoutFailure, ClientlessUnknownError } = AuthErrorNumeral

    switch (category) {
      case Config:
        return ClientlessConfigServiceException

      case Authentication:
        return ClientlessAuthNTokenServiceException

      case Authorization:
        return ClientlessAuthorizeServiceException

      case Preauthorization:
        return ClientlessPreauthorizeServiceException

      case Logout:
        return AdobeLogoutFailure

      default:
        return ClientlessUnknownError
    }
  }

  private static getRecoverySuggestion(action: RestErrorAction): string | undefined {
    switch (action) {
      case 'none':
        return 'There is no predefined action to remediate this issue, but in some cases this might indicate an improper invocation of the API'
      case 'configuration':
        return 'The client application requires a configuration change, most of the time conducted through Adobe Pass TVE Dashboard'
      case 'application-registration':
        return 'The client application requires to register itself again'
      case 'authentication':
        return 'The client application requires to authenticate or re-authenticate the user'
      case 'authorization':
        return 'The client application requires to obtain authorization for the specified resource'
      case 'retry':
        return 'The client application requires to retry the request'
      default:
        console.debug(`[ClientlessAuthErrorFactory.getRecoverySuggestion] received an unknown action: '${action}'`)
        return undefined
    }
  }

  private static getCategory(value: AuthErrorCategory): string {
    for (const key in AuthErrorCategory) {
      if (AuthErrorCategory[key as keyof typeof AuthErrorCategory] === value) {
        return `'${value}' (${key})`
      }
    }

    return `'${value}'`
  }
}
